// The ISS for the /iss/ page and its pass alerts: where it is now, its
// ground track, and the passes an observer can see. SGP4 from satellite.js
// on CelesTrak's element set. Pure ESM with no DOM, shared by the page
// (src/site/iss/main.js) and the daily alert job (api/cron/iss-alerts.js).
//
// satellite.js's files are imported by path: the package entry also pulls
// in its WASM bulk runtime, which neither the page bundle nor the function
// needs (src/satellites/worker.js does the same).

import { json2satrec } from '../../node_modules/satellite.js/dist/io.js';
import { propagate } from '../../node_modules/satellite.js/dist/propagation/propagate.js';
import { gstime } from '../../node_modules/satellite.js/dist/propagation/gstime.js';
import { eciToEcf, eciToGeodetic, geodeticToEcf } from '../../node_modules/satellite.js/dist/transforms.js';
import { sunPos } from '../../node_modules/satellite.js/dist/sun.js';
import { jday } from '../../node_modules/satellite.js/dist/ext.js';
import { shadowFraction } from '../../node_modules/satellite.js/dist/shadow.js';
import { rowToOmm } from '../satellites/catalog.js';

export const ISS_NORAD = 25544;
const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;
const EARTH_KM = 6378.137;

// A satellite.js record from a catalogue row (src/satellites/catalog.js) or
// a CelesTrak OMM object.
export function satrecFrom(rowOrOmm) {
  return json2satrec(Array.isArray(rowOrOmm) ? rowToOmm(rowOrOmm) : rowOrOmm);
}

// Element set epoch, ms.
export function epochMs(rowOrOmm) {
  if (Array.isArray(rowOrOmm)) return rowOrOmm[3];
  const e = String(rowOrOmm.EPOCH || '');
  return Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(e) ? e : `${e}Z`);
}

const wrapLon = (lon) => ((((lon + 180) % 360) + 360) % 360) - 180;

// Where the ISS is at `date`: latitude, longitude, height (km), ground speed
// over the turning Earth (km/h), and whether it is in sunlight.
export function issState(satrec, date = new Date()) {
  const pv = propagate(satrec, date);
  if (!pv || !pv.position) return null;
  const gmst = gstime(date);
  const gd = eciToGeodetic(pv.position, gmst);
  const v = pv.velocity;
  // Inertial speed; the ground track speed differs by the Earth's turn, but
  // the figure people quote (27,600 km/h) is this one.
  const speed = Math.hypot(v.x, v.y, v.z) * 3600;
  const sun = sunPos(jday(date)).rsun;
  const shade = shadowFraction(sun, pv.position);
  return {
    time: date.getTime(),
    lat: gd.latitude * R2D,
    lon: wrapLon(gd.longitude * R2D),
    altKm: gd.height,
    speedKmh: speed,
    sunlit: shade < 0.5,
    eci: pv.position,
  };
}

// TEME position and velocity (km, km/s) and Greenwich sidereal time (radians)
// at a date, for placing the station in a 3D scene (the viewer's ISS model,
// src/main.js placeIssFromOrbit).
export function issTeme(satrec, date) {
  const pv = propagate(satrec, date);
  if (!pv || !pv.position || !pv.velocity) return null;
  return { r: pv.position, v: pv.velocity, gmst: gstime(date) };
}

// Latitude and longitude every `stepSec` seconds from `fromMs` to `toMs`.
export function groundTrack(satrec, fromMs, toMs, stepSec = 30) {
  const out = [];
  for (let t = fromMs; t <= toMs; t += stepSec * 1000) {
    const d = new Date(t);
    const pv = propagate(satrec, d);
    if (!pv || !pv.position) continue;
    const gd = eciToGeodetic(pv.position, gstime(d));
    out.push({ t, lat: gd.latitude * R2D, lon: wrapLon(gd.longitude * R2D), altKm: gd.height });
  }
  return out;
}

// One run of samples, shared by every observer: the ISS in Earth-fixed km,
// the Sun's direction (Earth-fixed, unit) and whether the ISS is sunlit.
export function sampleOrbit(satrec, fromMs, hours, stepSec = 20) {
  const n = Math.floor((hours * 3600) / stepSec) + 1;
  const t = new Float64Array(n);
  const pos = new Float64Array(n * 3);
  const sun = new Float64Array(n * 3);
  const lit = new Uint8Array(n);
  const ok = new Uint8Array(n);
  let sunEcf = null;
  for (let i = 0; i < n; i++) {
    const ms = fromMs + i * stepSec * 1000;
    t[i] = ms;
    const d = new Date(ms);
    const pv = propagate(satrec, d);
    if (!pv || !pv.position) continue;
    const gmst = gstime(d);
    const ecf = eciToEcf(pv.position, gmst);
    pos[i * 3] = ecf.x; pos[i * 3 + 1] = ecf.y; pos[i * 3 + 2] = ecf.z;
    // The Sun moves a quarter of a degree an hour against the stars, but the
    // Earth turns under it; recompute every sample, it is cheap.
    const rs = sunPos(jday(d)).rsun;
    sunEcf = eciToEcf(rs, gmst);
    const len = Math.hypot(sunEcf.x, sunEcf.y, sunEcf.z);
    sun[i * 3] = sunEcf.x / len; sun[i * 3 + 1] = sunEcf.y / len; sun[i * 3 + 2] = sunEcf.z / len;
    // Visible while any of it is lit, as Heavens-Above counts it.
    lit[i] = shadowFraction(rs, pv.position) < 0.99 ? 1 : 0;
    ok[i] = 1;
  }
  return { t, pos, sun, lit, ok, n, stepSec };
}

// An observer's place in Earth-fixed km and its local east, north and up.
function observerFrame(lat, lon, heightKm = 0) {
  const la = lat * D2R, lo = lon * D2R;
  const o = geodeticToEcf({ latitude: la, longitude: lo, height: heightKm });
  const up = [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)];
  const east = [-Math.sin(lo), Math.cos(lo), 0];
  const north = [-Math.sin(la) * Math.cos(lo), -Math.sin(la) * Math.sin(lo), Math.cos(la)];
  return { o: [o.x, o.y, o.z], up, east, north };
}

const COMPASS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
export const compass = (az) => COMPASS[Math.round((((az % 360) + 360) % 360) / 22.5) % 16];

// How bright the ISS looks: a standard magnitude at 1,000 km and half lit,
// scaled by distance and by how much of its sunlit side faces the observer,
// with the phase law (1 + cos phase) / 2. A standard magnitude of -2.0
// matches Heavens-Above's figures for fourteen Sydney passes to 0.26 mag
// (rms). An estimate: the solar panels make the station flare and fade.
function magnitude(rangeKm, phase) {
  const g = (1 + Math.cos(phase)) / 2;
  return -2.0 + 5 * Math.log10(rangeKm / 1000) - 2.5 * Math.log10(Math.max(1e-3, g) / 0.5);
}

// The visible passes for an observer in a sampled run. A pass counts when
// the ISS is at least `minEl` degrees up, in sunlight, while the observer's
// sky is dark enough (the Sun more than 6 degrees below the horizon, civil
// twilight's end). Times are the visible stretch: when it appears, when it
// is highest, when it fades or sets.
//
// With `satrec`, each pass's highest point is found to the second (a pass
// near the zenith changes elevation fast, and 20-second samples cut the top
// off it).
export function visiblePasses(samples, { lat, lon, heightKm = 0 }, { minEl = 10, sunAlt = -6, minPeak = 10, satrec = null } = {}) {
  const { t, pos, sun, lit, ok, n } = samples;
  const f = observerFrame(lat, lon, heightKm);
  const minSin = Math.sin(minEl * D2R);
  const sunSin = Math.sin(sunAlt * D2R);
  const el = new Float64Array(n);
  const az = new Float64Array(n);
  const range = new Float64Array(n);
  const visible = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    if (!ok[i]) { el[i] = -90; continue; }
    const dx = pos[i * 3] - f.o[0], dy = pos[i * 3 + 1] - f.o[1], dz = pos[i * 3 + 2] - f.o[2];
    const r = Math.hypot(dx, dy, dz);
    const u = (dx * f.up[0] + dy * f.up[1] + dz * f.up[2]) / r;
    const e = dx * f.east[0] + dy * f.east[1];
    const no = dx * f.north[0] + dy * f.north[1] + dz * f.north[2];
    el[i] = Math.asin(u) * R2D;
    az[i] = ((Math.atan2(e, no) * R2D) + 360) % 360;
    range[i] = r;
    const sunUp = sun[i * 3] * f.up[0] + sun[i * 3 + 1] * f.up[1] + sun[i * 3 + 2] * f.up[2];
    visible[i] = u >= minSin && lit[i] && sunUp < sunSin ? 1 : 0;
  }
  // Interpolated crossing time between samples i-1 and i for a level.
  const cross = (i, level) => {
    const a = el[i - 1], b = el[i];
    const k = b === a ? 0 : (level - a) / (b - a);
    return t[i - 1] + Math.max(0, Math.min(1, k)) * (t[i] - t[i - 1]);
  };
  const passes = [];
  let i = 0;
  while (i < n) {
    if (!visible[i]) { i++; continue; }
    const a = i;
    while (i < n && visible[i]) i++;
    const b = i - 1;
    // Highest visible sample, refined with a parabola through its neighbours.
    let m = a;
    for (let k = a; k <= b; k++) if (el[k] > el[m]) m = k;
    let peakT = t[m], peakEl = el[m];
    if (m > a && m < b) {
      const y0 = el[m - 1], y1 = el[m], y2 = el[m + 1];
      const den = y0 - 2 * y1 + y2;
      if (den < 0) {
        const off = 0.5 * (y0 - y2) / den;
        peakT = t[m] + off * (t[m] - t[m - 1]);
        peakEl = y1 - 0.25 * (y0 - y2) * off;
      }
    }
    if (satrec && m > a && m < b) {
      const top = refinePeak(satrec, f, t[m - 1], t[m + 1]);
      if (top && top.el > peakEl) { peakT = top.t; peakEl = top.el; }
    }
    if (peakEl < minPeak) continue;
    // Where it appears: rising through minEl, or out of the Earth's shadow
    // (found to the second with satrec), and where it fades or sets.
    const shadowStart = a > 0 && el[a - 1] >= minEl && !lit[a - 1];
    const endsInShadow = b + 1 < n && el[b + 1] >= minEl && !lit[b + 1];
    let startT = a > 0 && el[a - 1] < minEl && el[a] >= minEl ? cross(a, minEl) : t[a];
    let endT = b + 1 < n && el[b + 1] < minEl && el[b] >= minEl ? cross(b + 1, minEl) : t[b];
    let startEl = Math.max(minEl, el[a]), endEl = Math.max(minEl, el[b]);
    if (satrec && shadowStart) { const e = shadowEdge(satrec, f, t[a - 1], t[a]); startT = e.t; startEl = e.el; }
    if (satrec && endsInShadow) { const e = shadowEdge(satrec, f, t[b + 1], t[b]); endT = e.t; endEl = e.el; }
    if (startEl > peakEl) { peakEl = startEl; peakT = startT; }
    if (endEl > peakEl) { peakEl = endEl; peakT = endT; }
    // Brightness at the best moment: the closest sunlit point.
    let best = a;
    for (let k = a; k <= b; k++) if (range[k] < range[best]) best = k;
    const sx = sun[best * 3], sy = sun[best * 3 + 1], sz = sun[best * 3 + 2];
    const ox = f.o[0] - pos[best * 3], oy = f.o[1] - pos[best * 3 + 1], oz = f.o[2] - pos[best * 3 + 2];
    const phase = Math.acos(Math.max(-1, Math.min(1, (sx * ox + sy * oy + sz * oz) / range[best])));
    passes.push({
      start: Math.round(startT), peak: Math.round(peakT), end: Math.round(endT),
      startAz: az[a], startEl, peakAz: peakT === startT ? az[a] : peakT === endT ? az[b] : az[m], peakEl,
      endAz: az[b], endEl, endsInShadow,
      duration: Math.round((endT - startT) / 1000),
      mag: Math.round(magnitude(range[best], phase) * 10) / 10,
      rangeKm: Math.round(range[best]),
      phaseDeg: Math.round(phase * R2D),
    });
  }
  return passes;
}

// Elevation (degrees) of the ISS for an observer frame at a moment.
function elevationAt(satrec, f, ms) {
  const d = new Date(ms);
  const pv = propagate(satrec, d);
  if (!pv || !pv.position) return -90;
  const p = eciToEcf(pv.position, gstime(d));
  const dx = p.x - f.o[0], dy = p.y - f.o[1], dz = p.z - f.o[2];
  return Math.asin((dx * f.up[0] + dy * f.up[1] + dz * f.up[2]) / Math.hypot(dx, dy, dz)) * R2D;
}

// The moment, to a second, the ISS crosses the edge of the Earth's shadow
// between a time in shadow (dark) and one in sunlight (lit).
function shadowEdge(satrec, f, dark, lit) {
  let a = dark, b = lit;
  while (Math.abs(b - a) > 500) {
    const m = (a + b) / 2;
    const d = new Date(m);
    const pv = propagate(satrec, d);
    const shade = pv && pv.position ? shadowFraction(sunPos(jday(d)).rsun, pv.position) : 1;
    if (shade < 0.99) b = m; else a = m;
  }
  return { t: Math.round(b), el: elevationAt(satrec, f, b) };
}

// The highest point between two times, by golden-section search to a second.
function refinePeak(satrec, f, lo, hi) {
  const g = (Math.sqrt(5) - 1) / 2;
  let a = lo, b = hi;
  let c = b - g * (b - a), d = a + g * (b - a);
  let fc = elevationAt(satrec, f, c), fd = elevationAt(satrec, f, d);
  while (b - a > 1000) {
    if (fc > fd) { b = d; d = c; fd = fc; c = b - g * (b - a); fc = elevationAt(satrec, f, c); }
    else { a = c; c = d; fc = fd; d = a + g * (b - a); fd = elevationAt(satrec, f, d); }
  }
  const t = (a + b) / 2;
  return { t, el: elevationAt(satrec, f, t) };
}

// Where the ISS stands in an observer's sky at a moment: azimuth and
// elevation in degrees (for drawing a pass across the sky).
export function lookAngles(satrec, { lat, lon, heightKm = 0 }, ms) {
  const f = observerFrame(lat, lon, heightKm);
  const d = new Date(ms);
  const pv = propagate(satrec, d);
  if (!pv || !pv.position) return null;
  const p = eciToEcf(pv.position, gstime(d));
  const dx = p.x - f.o[0], dy = p.y - f.o[1], dz = p.z - f.o[2];
  const r = Math.hypot(dx, dy, dz);
  const el = Math.asin((dx * f.up[0] + dy * f.up[1] + dz * f.up[2]) / r) * R2D;
  const az = ((Math.atan2(dx * f.east[0] + dy * f.east[1], dx * f.north[0] + dy * f.north[1] + dz * f.north[2]) * R2D) + 360) % 360;
  return { az, el, rangeKm: r };
}

// Plain words for a pass's brightness.
export function brightnessWords(mag) {
  if (mag <= -3) return 'Brilliant';
  if (mag <= -2) return 'Very bright';
  if (mag <= -1) return 'Bright';
  return 'Easy to see';
}

export { EARTH_KM };
